import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Field, Formik } from "formik";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { green } from "@mui/material/colors";
import Form from "@poseidon/ui/components/form/FullWidthForm";
import { FullWidthField } from "@poseidon/ui/components";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import { CreateSailorSchema } from "@poseidon/utils/authFieldUtils";
import { useCreateSailorAccount } from "../hooks/useCreateSailorAccount";
import { GeneratedPasswordPanel } from "../components/GeneratedPasswordPanel";

const initialValues = {
  email: "",
  firstName: "",
  lastName: "",
};

/**
 * Create a sailor (auth user + sailors/{uid} doc + sailor claim) via the
 * admin-only `adminCreateSailorAccount` callable. On success the dialog stays
 * open to show the one-time password, then offers to open the new sailor.
 */
export function CreateSailorModal({ open, onClose }) {
  const navigate = useNavigate();
  const { setToast } = useToastMessage();
  const { createAccount } = useCreateSailorAccount();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(undefined);
  const [created, setCreated] = useState(null);

  const handleClose = () => {
    if (busy) {
      return;
    }
    setError(undefined);
    setCreated(null);
    onClose();
  };

  const handleSubmit = async (values) => {
    setError(undefined);
    setBusy(true);
    try {
      const { uid, password } = await createAccount({
        email: values.email.trim(),
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      });
      setCreated({ uid, password });
      setToast("Sailor created");
    } catch (err) {
      setError(err.message ?? "Failed to create sailor");
    } finally {
      setBusy(false);
    }
  };

  const goToSailor = () => {
    const { uid } = created;
    handleClose();
    navigate(`/sailors/${uid}`);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Sailor</DialogTitle>
      {created ? (
        <>
          <DialogContent>
            <Alert severity="success">Sailor account created.</Alert>
            <GeneratedPasswordPanel password={created.password} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
            <Button variant="contained" color="primary" onClick={goToSailor}>
              Go to sailor
            </Button>
          </DialogActions>
        </>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={CreateSailorSchema}
          onSubmit={handleSubmit}
        >
          <Form>
            <DialogContent>
              <Field
                component={FullWidthField}
                name="email"
                label="Email"
                margin="normal"
                autoFocus
                disabled={busy}
              />
              <Field
                component={FullWidthField}
                name="firstName"
                label="First Name"
                margin="normal"
                disabled={busy}
              />
              <Field
                component={FullWidthField}
                name="lastName"
                label="Last Name"
                margin="normal"
                disabled={busy}
              />
              <Alert severity="warning" sx={{ mt: 2 }}>
                A new user account will be created. A one-time password will be
                generated and shown once — share it with the sailor so they can
                sign in.
              </Alert>
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={busy}>
                Cancel
              </Button>
              <Box sx={{ position: "relative" }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={busy}
                >
                  Create
                </Button>
                {busy && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: green[500],
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      mt: "-12px",
                      ml: "-12px",
                    }}
                  />
                )}
              </Box>
            </DialogActions>
          </Form>
        </Formik>
      )}
    </Dialog>
  );
}
